/**
 * Serialize / deserialize train statistics query params for shareable URLs.
 * API always uses the destination stop (arrival) as stopId; origin/destination
 * ids are both required for UI labels and toggling to departure stats in-session.
 */

export type TrainStatsUrlPayload = {
  line: string;
  originId: string;
  destinationId: string;
  originName?: string;
  destinationName?: string;
};

const KEYS = {
  line: 'line',
  originId: 'originId',
  destinationId: 'destinationId',
  originName: 'originName',
  destinationName: 'destinationName',
} as const;

export function serializeTrainStatsToSearchParams(
  payload: TrainStatsUrlPayload
): URLSearchParams {
  const params = new URLSearchParams();
  params.set(KEYS.line, payload.line);
  params.set(KEYS.originId, payload.originId);
  params.set(KEYS.destinationId, payload.destinationId);
  if (payload.originName) params.set(KEYS.originName, payload.originName);
  if (payload.destinationName) params.set(KEYS.destinationName, payload.destinationName);
  return params;
}

/** Returns null if required API fields are missing (invalid or empty URL payload). */
export function deserializeTrainStatsFromSearchParams(
  searchParams: URLSearchParams
): TrainStatsUrlPayload | null {
  const line = searchParams.get(KEYS.line)?.trim() ?? '';
  const originId = searchParams.get(KEYS.originId)?.trim() ?? '';
  const destinationId = searchParams.get(KEYS.destinationId)?.trim() ?? '';
  const originName = searchParams.get(KEYS.originName)?.trim() ?? '';
  const destinationName = searchParams.get(KEYS.destinationName)?.trim() ?? '';

  if (!line || !originId || !destinationId) {
    return null;
  }

  return {
    line,
    originId,
    destinationId,
    ...(originName ? { originName } : {}),
    ...(destinationName ? { destinationName } : {}),
  };
}

/** Minimal leg-shaped object for getLineName, headers, charts, and FeedbackCard. */
export function buildLegStubFromTrainUrlPayload(payload: TrainStatsUrlPayload) {
  return {
    line: { name: payload.line },
    origin: {
      id: payload.originId,
      name: payload.originName ?? payload.originId,
    },
    destination: {
      id: payload.destinationId,
      name: payload.destinationName ?? payload.destinationId,
    },
  };
}

export function trainStatsPayloadFromLeg(leg: {
  line: { name: string };
  origin: { id: string; name: string };
  destination: { id: string; name: string };
}): TrainStatsUrlPayload {
  return {
    line: leg.line.name,
    originId: String(leg.origin.id),
    destinationId: String(leg.destination.id),
    originName: leg.origin.name,
    destinationName: leg.destination.name,
  };
}
